/**
 * Data Limiting Strategies for Report Charts
 *
 * This module provides functions to limit report data based on different strategies:
 * - Time Series: Keep top N groups across recent time periods
 * - Hierarchical: Keep top N X-axis groups and their series
 * - None: No limiting (e.g., for funnel charts)
 */

import { DimensionName, DIMENSIONS, DimensionV2 } from "@/common/dimensions.ts";
import { MeasureName } from "@/common/measures.ts";

export interface DataItem {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  value: number;
  value_num_employees?: number;
}

type LimitStrategy = "time-series" | "hierarchical" | "none";

export interface LimitConfig {
  limit: number;
  chartType: string;
  dimensions: DimensionName[];
  measures: MeasureName[];
}

interface GroupedDataItems {
  items: DataItem[];
  total: number;
}

/**
 * Limits data based on chart configuration strategy
 *
 * @param data - Array of data items from SQL query
 * @param config - Configuration specifying limit amount and context
 * @param timeDimension - Optional time dimension if present
 * @returns Limited data array
 */
export function limitReportData(
  data: DataItem[],
  config: LimitConfig,
  recruitmentStageNames: string[],
  timeDimension?: DimensionV2,
): DataItem[] {
  if (!data?.length || !config.limit) return data;

  const strategy = determineStrategy(config, timeDimension);
  switch (strategy) {
    case "time-series":
      return limitByTimeSeries(data, config, timeDimension!);
    case "hierarchical":
      return limitByHierarchy(data, config, recruitmentStageNames);
    case "none":
      return data;
  }
}

/**
 * Determines which limiting strategy to use based on configuration
 */
function determineStrategy(
  config: LimitConfig,
  timeDimension?: DimensionV2,
): LimitStrategy {
  if (timeDimension) return "time-series";
  if (config.chartType === "Funnel" || config.chartType === "Insights Wheel")
    return "none";
  return "hierarchical";
}

/**
 * Limits time-series data to the N most recent time periods
 *
 * Algorithm:
 * 1. Get N most recent time periods
 * 2. Filter data to those periods
 * 3. Return matching data points, sorted chronologically
 *
 * Example: For "Headcount by Gender over Time" with limit=3
 * - Selects 3 most recent quarters
 * - Returns all groups (e.g. all genders) within those quarters
 */
function limitByTimeSeries(
  data: DataItem[],
  config: LimitConfig,
  timeDimension: DimensionV2,
): DataItem[] {
  const timeField = timeDimension.field;

  // Step 1: Get N most recent time periods
  const uniquePeriods = [...new Set(data.map((item) => item[timeField]))]
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    .slice(0, config.limit);

  // Step 2: Filter to recent periods only, sorted chronologically
  return data
    .filter((item) => uniquePeriods.includes(item[timeField]))
    .sort(
      (a, b) =>
        new Date(a[timeField]).getTime() - new Date(b[timeField]).getTime(),
    );
}

/**
 * Limits hierarchical data by selecting top N X-axis groups
 *
 * Algorithm:
 * 1. Group rows by the last dimension, which is the X-axis dimension
 * 2. Rank X-axis groups by total value or custom sort rules
 * 3. Keep top N X-axis groups
 * 4. Return all rows inside selected groups (do not trim interior rows)
 *
 * Example: For "Engagement by Department > Color" with limit=10
 * - Keep top 10 departments
 * - Preserve all color rows inside each selected department
 * - Result: no missing interior values (e.g., red/blue/green/yellow)
 */
function limitByHierarchy(
  data: DataItem[],
  config: LimitConfig,
  recruitmentStageNames: string[] = [],
): DataItem[] {
  const orderedDimensions = config.dimensions.map(
    (dim) => DIMENSIONS[dim as keyof typeof DIMENSIONS],
  );

  if (orderedDimensions.length === 0) {
    // Single measure, no dimensions - just sort and limit
    return data.sort((a, b) => b.value - a.value).slice(0, config.limit);
  }
  // The chart transformer uses the last configured dimension as the X-axis.
  const xAxisDimension = orderedDimensions[orderedDimensions.length - 1];

  const getGroupKey = (item: DataItem): string =>
    String(item[xAxisDimension.field]);

  const groupedItems = Array.from(groupBy(data, getGroupKey).values()).map(
    (items) => ({
      items,
      total: sumBy(items, (item) => item.value),
    }),
  );

  const sortingFunction = getGroupSortingFunction(
    xAxisDimension,
    recruitmentStageNames,
  );

  // Keep all series rows for selected X-axis groups.
  return groupedItems
    .sort(sortingFunction)
    .slice(0, config.limit)
    .flatMap((group) => group.items);
}

/**
 * Groups array items by a key function
 */
function groupBy<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>();
  items.forEach((item) => {
    const key = keyFn(item);
    const group = map.get(key) ?? [];
    group.push(item);
    map.set(key, group);
  });
  return map;
}

/**
 * Sums array items by a value function
 */
function sumBy<T>(items: T[], valueFn: (item: T) => number): number {
  return items.reduce((sum, item) => sum + valueFn(item), 0);
}

export const LAG_TIME_BUCKET_PRECEDENCE = {
  "Same Day": 0,
  "1-2 Days": 1,
  "3-7 Days": 2,
  "8-14 Days": 3,
  "15+ Days": 4,
} as const;

export const compareLagTimeBucketLabels = (a: string, b: string): number => {
  const aIndex =
    LAG_TIME_BUCKET_PRECEDENCE[a as keyof typeof LAG_TIME_BUCKET_PRECEDENCE] ??
    Infinity;
  const bIndex =
    LAG_TIME_BUCKET_PRECEDENCE[b as keyof typeof LAG_TIME_BUCKET_PRECEDENCE] ??
    Infinity;
  return aIndex - bIndex;
};

const sortByProperty =
  (property: string) =>
  (
    a: { [key: string]: any }, // eslint-disable-line @typescript-eslint/no-explicit-any
    b: { [key: string]: any }, // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    b[property] - a[property];

const sortByRecruitmentStage = (
  recruitmentStageNames: string[],
  dimension: DimensionName,
) => {
  return (
    a: { [key: string]: any }, // eslint-disable-line @typescript-eslint/no-explicit-any
    b: { [key: string]: any }, // eslint-disable-line @typescript-eslint/no-explicit-any
  ) => {
    const propName =
      dimension === "RECRUITMENT_STAGE_HISTORICAL"
        ? "RECRUITMENT_STAGE"
        : "Current_Stage";
    const aStage = "items" in a ? a.items[0][propName] : a[propName];
    const bStage = "items" in b ? b.items[0][propName] : b[propName];
    const aIndex = recruitmentStageNames.indexOf(aStage);
    const bIndex = recruitmentStageNames.indexOf(bStage);
    return aIndex - bIndex;
  };
};

function getGroupSortingFunction(
  dimension: DimensionV2,
  recruitmentStageNames: string[],
): (a: GroupedDataItems, b: GroupedDataItems) => number {
  switch (dimension.dimension) {
    case "RECRUITMENT_STAGE":
    case "RECRUITMENT_STAGE_HISTORICAL":
      // Keep configured recruitment stage ordering.
      return sortByRecruitmentStage(recruitmentStageNames, dimension.dimension);
    case "PERFORMANCE_RATING":
      // Keep alphabetical ordering for performance ratings.
      return (a: GroupedDataItems, b: GroupedDataItems) => {
        const aRating = a.items[0][dimension.field] || "";
        const bRating = b.items[0][dimension.field] || "";
        return aRating.localeCompare(bRating);
      };
    case "ANSWER_VALUE":
      // Sort survey answer values numerically when possible.
      return (a: GroupedDataItems, b: GroupedDataItems) => {
        const aScore = parseFloat(a.items[0][dimension.field]) || 0;
        const bScore = parseFloat(b.items[0][dimension.field]) || 0;
        return aScore - bScore;
      };
    case "LAG_TIME_BUCKET": {
      return (a: GroupedDataItems, b: GroupedDataItems) => {
        const aLag = String(a.items[0][dimension.field] ?? "");
        const bLag = String(b.items[0][dimension.field] ?? "");
        return compareLagTimeBucketLabels(aLag, bLag);
      };
    }
    default:
      return sortByProperty("total");
  }
}
